import { lazy, Suspense, type ComponentType } from 'react';
import { BrowserRouter, Navigate, Routes, Route } from 'react-router-dom';

import { LANDING_SECTION_SLUGS } from '@constants/publicSearch';

import { ToastProvider } from '@contexts/ToastProvider';
import { AuthProvider } from '@contexts/AuthProvider';
import { ThemeProvider } from '@contexts/ThemeProvider';
import { PublicSiteSettingsProvider } from '@contexts/PublicSiteSettingsProvider';
import { PublicSiteAnnouncementsProvider } from '@contexts/PublicSiteAnnouncementsProvider';
import { LandingPage } from '@pages/LandingPage';
import { MaintenanceGate } from '@components/features/app-layout/MaintenanceGate';
import { PublicSiteAnnouncementStrip } from '@components/features/app-layout/PublicSiteAnnouncementStrip';
import { RouteTracker } from '@components/features/app-layout/RouteTracker';
import { ScrollToTop } from '@components/features/app-layout/ScrollToTop';
import { SessionHandler } from '@components/features/app-layout/SessionHandler';
import { TelemetryUserSync } from '@components/features/app-layout/TelemetryUserSync';
import { RouteFallback } from '@components/features/app-layout/RouteFallback';
import { ErrorBoundary } from '@components/common/ErrorBoundary';

/**
 * Route-level code splitting: every page except the landing page is its own chunk, so the
 * first visit downloads only the shell + landing code (report/PDF/editor libraries load on
 * demand). Pages use named exports, hence the small adapter.
 */
function lazyPage<K extends string>(
  loader: () => Promise<Record<K, ComponentType>>,
  name: K
) {
  return lazy(() => loader().then((module) => ({ default: module[name] })));
}

/** Signed-in layouts (sidebar, header, notifications) load with the first page that needs them. */
const DashboardLayout = lazyPage(
  () => import('@components/features/app-layout/DashboardLayout'),
  'DashboardLayout'
);
const FamilyRegistrationLayout = lazyPage(
  () => import('@components/features/app-layout/FamilyRegistrationLayout'),
  'FamilyRegistrationLayout'
);
const EnrollLayout = lazyPage(() => import('@components/features/enroll/EnrollLayout'), 'EnrollLayout');
const SettingsLayout = lazyPage(() => import('@pages/settings/SettingsLayout'), 'SettingsLayout');

const RegisterPage = lazyPage(() => import('@pages/RegisterPage'), 'RegisterPage');
const LoginPage = lazyPage(() => import('@pages/LoginPage'), 'LoginPage');
const VerifyEmailPage = lazyPage(() => import('@pages/VerifyEmailPage'), 'VerifyEmailPage');
const VerifyPhonePage = lazyPage(() => import('@pages/VerifyPhonePage'), 'VerifyPhonePage');
const ForgotPasswordPage = lazyPage(() => import('@pages/ForgotPasswordPage'), 'ForgotPasswordPage');
const ResetPasswordPage = lazyPage(() => import('@pages/ResetPasswordPage'), 'ResetPasswordPage');
const ConfirmEmailChangePage = lazyPage(() => import('@pages/ConfirmEmailChangePage'), 'ConfirmEmailChangePage');
const CompleteInvitationPage = lazyPage(() => import('@pages/CompleteInvitationPage'), 'CompleteInvitationPage');
const TermsOfServicePage = lazyPage(() => import('@pages/TermsOfServicePage'), 'TermsOfServicePage');
const PrivacyPolicyPage = lazyPage(() => import('@pages/PrivacyPolicyPage'), 'PrivacyPolicyPage');
const Setup2FAPage = lazyPage(() => import('@pages/Setup2FAPage'), 'Setup2FAPage');
const OnboardingCompletePage = lazyPage(() => import('@pages/OnboardingCompletePage'), 'OnboardingCompletePage');
const DashboardPage = lazyPage(() => import('@pages/DashboardPage'), 'DashboardPage');
const FamilyRegistrationPage = lazyPage(() => import('@pages/FamilyRegistrationPage'), 'FamilyRegistrationPage');
const UsersPage = lazyPage(() => import('@pages/UsersPage'), 'UsersPage');
const UserProfilePage = lazyPage(() => import('@pages/UserProfilePage'), 'UserProfilePage');
const SemestersPage = lazyPage(() => import('@pages/SemestersPage'), 'SemestersPage');
const SemesterDetailPage = lazyPage(() => import('@pages/SemesterDetailPage'), 'SemesterDetailPage');
const SemesterClassesPage = lazyPage(() => import('@pages/SemesterClassesPage'), 'SemesterClassesPage');
const ClassesPage = lazyPage(() => import('@pages/ClassesPage'), 'ClassesPage');
const ClassFormPage = lazyPage(() => import('@pages/ClassFormPage'), 'ClassFormPage');
const ReviewClassesPage = lazyPage(() => import('@pages/ReviewClassesPage'), 'ReviewClassesPage');
const ClassReviewPage = lazyPage(() => import('@pages/ClassReviewPage'), 'ClassReviewPage');
const AuditLogPage = lazyPage(() => import('@pages/AuditLogPage'), 'AuditLogPage');
const RolesPage = lazyPage(() => import('@pages/RolesPage'), 'RolesPage');
const RoleEditPage = lazyPage(() => import('@pages/RoleEditPage'), 'RoleEditPage');
const RoleCreatePage = lazyPage(() => import('@pages/RoleCreatePage'), 'RoleCreatePage');
const RegistrationsPage = lazyPage(() => import('@pages/RegistrationsPage'), 'RegistrationsPage');
const FamilyDashboardPage = lazyPage(() => import('@pages/FamilyDashboardPage'), 'FamilyDashboardPage');
const EnrollPage = lazyPage(() => import('@pages/EnrollPage'), 'EnrollPage');
const EnrollBrowsePage = lazyPage(() => import('@pages/EnrollBrowsePage'), 'EnrollBrowsePage');
const EnrollClassDetailPage = lazyPage(() => import('@pages/EnrollClassDetailPage'), 'EnrollClassDetailPage');
const EnrollTutorProfilePage = lazyPage(() => import('@pages/EnrollTutorProfilePage'), 'EnrollTutorProfilePage');
const TutorProfileLegacyRedirect = lazyPage(() => import('@pages/TutorProfileLegacyRedirect'), 'TutorProfileLegacyRedirect');
const ClassRosterPage = lazyPage(() => import('@pages/ClassRosterPage'), 'ClassRosterPage');
const ReportsPage = lazyPage(() => import('@pages/ReportsPage'), 'ReportsPage');
const AttendancePerClassReportPage = lazyPage(() => import('@pages/reports/AttendancePerClassReportPage'), 'AttendancePerClassReportPage');
const BhgFullAttendanceReportPage = lazyPage(() => import('@pages/reports/BhgFullAttendanceReportPage'), 'BhgFullAttendanceReportPage');
const LocationByZipReportPage = lazyPage(() => import('@pages/reports/LocationByZipReportPage'), 'LocationByZipReportPage');
const LocationByCityReportPage = lazyPage(() => import('@pages/reports/LocationByCityReportPage'), 'LocationByCityReportPage');
const ChurchesReportPage = lazyPage(() => import('@pages/reports/ChurchesReportPage'), 'ChurchesReportPage');
const SpecialNotesFamiliesReportPage = lazyPage(() => import('@pages/reports/SpecialNotesFamiliesReportPage'), 'SpecialNotesFamiliesReportPage');
const ChildrenAllergiesReportPage = lazyPage(() => import('@pages/reports/ChildrenAllergiesReportPage'), 'ChildrenAllergiesReportPage');
const EmergencyContactsReportPage = lazyPage(() => import('@pages/reports/EmergencyContactsReportPage'), 'EmergencyContactsReportPage');
const ChildrenTutorNotesReportPage = lazyPage(() => import('@pages/reports/ChildrenTutorNotesReportPage'), 'ChildrenTutorNotesReportPage');
const EnrollmentByGradeReportPage = lazyPage(() => import('@pages/reports/EnrollmentByGradeReportPage'), 'EnrollmentByGradeReportPage');
const ClassCapacityReportPage = lazyPage(() => import('@pages/reports/ClassCapacityReportPage'), 'ClassCapacityReportPage');
const RegistrationCompletionReportPage = lazyPage(() => import('@pages/reports/RegistrationCompletionReportPage'), 'RegistrationCompletionReportPage');
const AttendanceRateSummaryReportPage = lazyPage(() => import('@pages/reports/AttendanceRateSummaryReportPage'), 'AttendanceRateSummaryReportPage');
const OutstandingPaymentsReportPage = lazyPage(() => import('@pages/reports/OutstandingPaymentsReportPage'), 'OutstandingPaymentsReportPage');
const SemesterMoneyReportPage = lazyPage(() => import('@pages/reports/SemesterMoneyReportPage'), 'SemesterMoneyReportPage');
const FamiliesPerSemesterReportPage = lazyPage(() => import('@pages/reports/FamiliesPerSemesterReportPage'), 'FamiliesPerSemesterReportPage');
const NewAccountsByMonthReportPage = lazyPage(() => import('@pages/reports/NewAccountsByMonthReportPage'), 'NewAccountsByMonthReportPage');
const LoginVisitsReportPage = lazyPage(() => import('@pages/reports/LoginVisitsReportPage'), 'LoginVisitsReportPage');
const GoogleContactsExportReportPage = lazyPage(() => import('@pages/reports/GoogleContactsExportReportPage'), 'GoogleContactsExportReportPage');
const NotificationSettingsPage = lazyPage(() => import('@pages/settings/NotificationSettingsPage'), 'NotificationSettingsPage');
const AccountSettingsPage = lazyPage(() => import('@pages/settings/AccountSettingsPage'), 'AccountSettingsPage');
const AnnouncementsSettingsPage = lazyPage(() => import('@pages/settings/AnnouncementsSettingsPage'), 'AnnouncementsSettingsPage');
const AppSettingsPage = lazyPage(() => import('@pages/settings/AppSettingsPage'), 'AppSettingsPage');
const HelpPage = lazyPage(() => import('@pages/HelpPage'), 'HelpPage');
const HelpArticlePage = lazyPage(() => import('@pages/HelpArticlePage'), 'HelpArticlePage');
const NotFoundPage = lazyPage(() => import('@pages/NotFoundPage'), 'NotFoundPage');
const ForbiddenPage = lazyPage(() => import('@pages/ForbiddenPage'), 'ForbiddenPage');
const UnavailablePage = lazyPage(() => import('@pages/UnavailablePage'), 'UnavailablePage');

function App() {
  return (
    <ToastProvider>
      <ThemeProvider>
        {/* ErrorBoundary must wrap AuthProvider, not be inside it.
            A boundary only catches errors in its children — if AuthProvider
            itself throws, a boundary nested inside it will never see the error. */}
        <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <RouteTracker />
            <SessionHandler />
            <TelemetryUserSync />
            <PublicSiteSettingsProvider>
            <PublicSiteAnnouncementsProvider>
            <PublicSiteAnnouncementStrip />
            <MaintenanceGate>
            <ErrorBoundary>
            <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route element={<LandingPage />}>
                <Route index element={null} />
                {LANDING_SECTION_SLUGS.map((slug) => (
                  <Route key={slug} path={slug} element={null} />
                ))}
                <Route path="visit" element={null} />
              </Route>
              <Route path="/tutor/:userId" element={<TutorProfileLegacyRedirect />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/verify-phone" element={<VerifyPhonePage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/confirm-email-change" element={<ConfirmEmailChangePage />} />
              <Route path="/complete-invitation" element={<CompleteInvitationPage />} />
              <Route path="/terms" element={<TermsOfServicePage />} />
              <Route path="/privacy" element={<PrivacyPolicyPage />} />
              <Route path="/setup-2fa" element={<Setup2FAPage />} />
              <Route path="/onboarding-complete" element={<OnboardingCompletePage />} />
              <Route path="/dashboard" element={<DashboardLayout />}>
                <Route index element={<DashboardPage />} />
              </Route>
              <Route path="/family" element={<DashboardLayout />}>
                <Route index element={<FamilyDashboardPage />} />
              </Route>
              <Route path="/enroll" element={<DashboardLayout />}>
                <Route element={<EnrollLayout />}>
                  <Route index element={<EnrollPage />} />
                  <Route path="browse" element={<EnrollBrowsePage />} />
                  <Route path="class/:classId" element={<EnrollClassDetailPage />} />
                  <Route path="tutor/:userId" element={<EnrollTutorProfilePage />} />
                </Route>
              </Route>
              <Route path="/family-registration" element={<FamilyRegistrationLayout />}>
                <Route index element={<FamilyRegistrationPage />} />
              </Route>
              <Route path="/users" element={<DashboardLayout />}>
                <Route index element={<UsersPage />} />
                <Route path=":userId" element={<UserProfilePage />} />
              </Route>
              <Route path="/audit" element={<DashboardLayout />}>
                <Route index element={<AuditLogPage />} />
              </Route>
              <Route path="/registrations" element={<DashboardLayout />}>
                <Route index element={<RegistrationsPage />} />
              </Route>
              <Route path="/roles" element={<DashboardLayout />}>
                <Route index element={<RolesPage />} />
                <Route path="new" element={<RoleCreatePage />} />
                <Route path=":roleId/edit" element={<RoleEditPage />} />
              </Route>
              <Route path="/semesters" element={<DashboardLayout />}>
                <Route index element={<SemestersPage />} />
                <Route path=":semesterId/classes" element={<SemesterClassesPage />} />
                <Route path=":semesterId" element={<SemesterDetailPage />} />
              </Route>
              <Route path="/classes" element={<DashboardLayout />}>
                <Route index element={<ClassesPage />} />
                <Route path="review" element={<ReviewClassesPage />} />
                <Route path="new" element={<ClassFormPage />} />
                <Route path=":classId/edit" element={<ClassFormPage />} />
                <Route path=":classId/review" element={<ClassReviewPage />} />
                <Route path=":classId/roster" element={<ClassRosterPage />} />
              </Route>
              <Route path="/reports" element={<DashboardLayout />}>
                <Route index element={<ReportsPage />} />
                <Route path="attendance-per-class" element={<AttendancePerClassReportPage />} />
                <Route path="bhg-full-attendance" element={<BhgFullAttendanceReportPage />} />
                <Route path="location-by-zip" element={<LocationByZipReportPage />} />
                <Route path="location-by-city" element={<LocationByCityReportPage />} />
                <Route path="churches" element={<ChurchesReportPage />} />
                <Route path="special-notes-families" element={<SpecialNotesFamiliesReportPage />} />
                <Route path="children-allergies" element={<ChildrenAllergiesReportPage />} />
                <Route path="emergency-contacts" element={<EmergencyContactsReportPage />} />
                <Route path="children-tutor-notes" element={<ChildrenTutorNotesReportPage />} />
                <Route path="enrollment-by-grade" element={<EnrollmentByGradeReportPage />} />
                <Route path="class-capacity" element={<ClassCapacityReportPage />} />
                <Route
                  path="registration-completion"
                  element={<RegistrationCompletionReportPage />}
                />
                <Route
                  path="attendance-rate-summary"
                  element={<AttendanceRateSummaryReportPage />}
                />
                <Route path="outstanding-payments" element={<OutstandingPaymentsReportPage />} />
                <Route
                  path="semester-financial-report"
                  element={<SemesterMoneyReportPage />}
                />
                <Route
                  path="semester-money"
                  element={<Navigate to="/reports/semester-financial-report" replace />}
                />
                <Route path="families-per-semester" element={<FamiliesPerSemesterReportPage />} />
                <Route path="new-accounts-by-month" element={<NewAccountsByMonthReportPage />} />
                <Route path="login-visits" element={<LoginVisitsReportPage />} />
                <Route path="google-contacts-export" element={<GoogleContactsExportReportPage />} />
              </Route>
              <Route path="/help" element={<DashboardLayout />}>
                <Route index element={<HelpPage />} />
                <Route path=":articleId" element={<HelpArticlePage />} />
              </Route>
              <Route path="/settings" element={<DashboardLayout />}>
                <Route element={<SettingsLayout />}>
                  <Route path="account" element={<AccountSettingsPage />} />
                  <Route path="notifications" element={<NotificationSettingsPage />} />
                  <Route path="announcements" element={<AnnouncementsSettingsPage />} />
                  <Route path="app" element={<AppSettingsPage />} />
                </Route>
              </Route>
              <Route path="/forbidden" element={<ForbiddenPage />} />
              <Route path="/unavailable" element={<UnavailablePage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </Suspense>
            </ErrorBoundary>
            </MaintenanceGate>
            </PublicSiteAnnouncementsProvider>
            </PublicSiteSettingsProvider>
          </BrowserRouter>
        </AuthProvider>
        </ErrorBoundary>
      </ThemeProvider>
    </ToastProvider>
  );
}

export default App;
